import { useCallback, useEffect, useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import {
	MfaSessionExpiredError,
	completeTotpEnrollmentLogin,
	enrollPasskeyLogin,
	startTotpEnrollmentLogin,
	verifyPasskeyLogin,
	verifyRecoveryCodeLogin,
	verifyTotpLogin,
	type MfaChallenge,
	type RecoveryCodes,
} from "@/lib/mfa-api";
import { passkeySupportedInBrowser } from "@/lib/webauthn";
import { MfaVerifyForm, type MfaVerifier } from "./MfaVerifyForm";
import { RecoveryCodesPanel } from "./RecoveryCodesPanel";
import { TotpSetupForm } from "./TotpSetupForm";

const loginVerifier: MfaVerifier = {
	totp: verifyTotpLogin,
	recoveryCode: verifyRecoveryCodeLogin,
	passkey: verifyPasskeyLogin,
};

interface LoginMfaStepProps {
	challenge: MfaChallenge;
	onSignedIn: () => void;
	/** ログイン待ちが期限切れなど、パスワードからやり直してもらうとき。 */
	onRestart: (message: string) => void;
}

/** サインインの 2 段目。登録済みの手段で確認するか、組織が必須にしていれば手段を登録させる。 */
export function LoginMfaStep({ challenge, onSignedIn, onRestart }: LoginMfaStepProps) {
	/** null は確認中。確認前にリカバリーコード欄を出してすぐ隠す、というちらつきを避ける。 */
	const [browserSupportsPasskey, setBrowserSupportsPasskey] = useState<boolean | null>(null);

	useEffect(() => {
		passkeySupportedInBrowser()
			.then(setBrowserSupportsPasskey)
			.catch((err: unknown) => console.error("パスキーの対応状況を確認できませんでした", err));
	}, []);

	/** 期限切れならやり直しに回して true を返す。それ以外はその場で表示させる。 */
	const restartIfExpired = useCallback(
		(err: unknown) => {
			if (!(err instanceof MfaSessionExpiredError)) return false;
			onRestart(err.message);
			return true;
		},
		[onRestart],
	);

	return challenge.mode === "verify" ? (
		<MfaVerifyForm
			methods={challenge}
			passkeyUsable={challenge.passkey ? browserSupportsPasskey : false}
			verifier={loginVerifier}
			onVerified={onSignedIn}
			onFatalError={restartIfExpired}
		/>
	) : (
		<EnrollStep
			passkeyUsable={challenge.passkeyAvailable && browserSupportsPasskey === true}
			onSignedIn={onSignedIn}
			restartIfExpired={restartIfExpired}
		/>
	);
}

function EnrollStep({
	passkeyUsable,
	onSignedIn,
	restartIfExpired,
}: {
	passkeyUsable: boolean;
	onSignedIn: () => void;
	restartIfExpired: (err: unknown) => boolean;
}) {
	const [stage, setStage] = useState<"choose" | "totp">("choose");
	const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
	const [registering, setRegistering] = useState(false);
	const [error, setError] = useState<string | null>(null);

	function finish(codes: RecoveryCodes) {
		if (codes && codes.length > 0) setRecoveryCodes(codes);
		else onSignedIn();
	}

	async function registerPasskey() {
		setRegistering(true);
		setError(null);
		try {
			const codes = await enrollPasskeyLogin();
			if (codes !== undefined) finish(codes);
		} catch (err) {
			if (restartIfExpired(err)) return;
			setError(err instanceof Error ? err.message : "パスキーを登録できませんでした。");
		} finally {
			setRegistering(false);
		}
	}

	if (recoveryCodes)
		return (
			<RecoveryCodesPanel codes={recoveryCodes} onDone={onSignedIn} doneLabel="保存して続ける" />
		);

	if (stage === "totp")
		return (
			<TotpSetupForm
				start={startTotpEnrollmentLogin}
				complete={completeTotpEnrollmentLogin}
				onDone={finish}
				onFatalError={restartIfExpired}
				onCancel={() => setStage("choose")}
			/>
		);

	return (
		<div data-testid="mfa-enroll" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			<p
				style={{
					margin: 0,
					fontSize: "var(--text-ui)",
					color: "var(--fg-2)",
					lineHeight: "var(--leading-body)",
				}}
			>
				組織の設定により、多要素認証の設定が必要です。サインインのたびに、パスワードに加えてパスキーか認証アプリで本人確認します。
			</p>
			{passkeyUsable && (
				<ConfirmButton
					label="パスキーを登録"
					loading={registering}
					onClick={() => void registerPasskey()}
					data-testid="mfa-enroll-passkey"
				/>
			)}
			<ConfirmButton
				label="認証アプリを使う"
				variant={passkeyUsable ? "secondary" : "primary"}
				loading={false}
				disabled={registering}
				onClick={() => setStage("totp")}
				data-testid="mfa-enroll-totp"
			/>
			{error && (
				<p style={{ margin: 0, fontSize: "var(--text-caption)", color: "var(--danger)" }}>
					{error}
				</p>
			)}
		</div>
	);
}
